import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getOpeningBalances,
  deleteOpeningBalance,
  postOpeningBalance,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import { FaPlus, FaTrash, FaPaperPlane, FaEdit, FaEye, FaPrint, FaFileExcel, FaChevronDown, FaChevronUp, FaPaperclip, FaFileAlt, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish, BASE_URL } from '../../../../shared/utils.js/utils';

const emptyDetail = () => ({ account_id: "", debit: "", credit: "" });



// opening balances component
const OpeningBalances = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const {
    openingBalances,
    openingBalancesPagination,
    isLoading,
    error,
    success,
  } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [openingBalanceForPrint, setOpeningBalanceForPrint] = useState(null);
  const [openingBalanceForPreview, setOpeningBalanceForPreview] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [allOpeningBalances, setAllOpeningBalances] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allBalancesForPDF, setAllBalancesForPDF] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openingBalanceToDelete, setOpeningBalanceToDelete] = useState(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const pageSize = 10;
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.openingBalances');
  }, [setTitle]);

  // Fetch all opening balances on load with per_page = total (like JournalEntries)
  const fetchOpeningBalances = async () => {
    try {
      // First fetch to get total count
      const params = {
        page: 1,
        per_page: 10
      };

      const result = await dispatch(getOpeningBalances(params));
      if (getOpeningBalances.fulfilled && result.type === getOpeningBalances.fulfilled.type) {
        const response = result.payload;
        // Handle response structure: response.data.data (for list) or response.data (for single or list)
        const responseData = response?.data;
        // Check if data is nested (data.data) or direct (data)
        let balancesArray = [];
        if (Array.isArray(responseData?.data)) {
          balancesArray = responseData.data;
        } else if (Array.isArray(responseData)) {
          balancesArray = responseData;
        } else if (responseData && typeof responseData === 'object') {
          // Single item response - wrap in array
          if (responseData.id) {
            balancesArray = [responseData];
          } else if (Array.isArray(responseData.items)) {
            balancesArray = responseData.items;
          } else if (Array.isArray(responseData.opening_balances)) {
            balancesArray = responseData.opening_balances;
          }
        }
        const paginationData = responseData?.meta || response?.meta || response?.data?.meta || openingBalancesPagination || null;
        const total = paginationData?.total || (balancesArray.length > 0 ? balancesArray.length : 0);

        if (total > 0) {
          // Fetch all entries with per_page = total from response
          const allParams = {
            page: 1,
            per_page: total
          };

          const allResult = await dispatch(getOpeningBalances(allParams));
          if (getOpeningBalances.fulfilled && allResult.type === getOpeningBalances.fulfilled.type) {
            const allResponse = allResult.payload;
            const allResponseData = allResponse?.data;
            let balancesList = [];
            // Extract balances from response - handle nested structure
            if (Array.isArray(allResponseData?.data)) {
              balancesList = allResponseData.data;
            } else if (Array.isArray(allResponseData)) {
              balancesList = allResponseData;
            } else if (Array.isArray(allResponse)) {
              balancesList = allResponse;
            } else if (allResponseData && typeof allResponseData === 'object') {
              // Check if it's a single object with id (like {id: 27, ...})
              if (allResponseData.id && !Array.isArray(allResponseData)) {
                balancesList = [allResponseData];
              } else if (Array.isArray(allResponseData.items)) {
                balancesList = allResponseData.items;
              } else if (Array.isArray(allResponseData.opening_balances)) {
                balancesList = allResponseData.opening_balances;
              } else if (Array.isArray(allResponseData.data)) {
                balancesList = allResponseData.data;
              }
            }
            // Ensure we have all balances from the API
            setAllOpeningBalances(balancesList);
            setPagination(paginationData);
          }
        } else {
          // No data or total is 0
          setAllOpeningBalances([]);
          setPagination(null);
        }
      }
    } catch (err) {
    }
  };

  // Fetch all opening balances for PDF templates (per_page = total)
  const fetchAllOpeningBalancesForPDF = async () => {
    try {
      // First fetch to get total count
      const params = {
        page: 1,
        per_page: 10
      };

      const result = await dispatch(getOpeningBalances(params));
      if (getOpeningBalances.fulfilled && result.type === getOpeningBalances.fulfilled.type) {
        const response = result.payload;
        const responseData = response?.data;
        const paginationData = responseData?.meta || response?.meta || response?.data?.meta || openingBalancesPagination || null;
        const total = paginationData?.total || 0;

        if (total > 0) {
          // Fetch all entries with per_page = total from response
          const allParams = {
            page: 1,
            per_page: total
          };

          const allResult = await dispatch(getOpeningBalances(allParams));
          if (getOpeningBalances.fulfilled && allResult.type === getOpeningBalances.fulfilled.type) {
            const allResponse = allResult.payload;
            const allResponseData = allResponse?.data;
            let balancesList = [];
            // Extract balances from response - handle nested structure (same as fetchOpeningBalances)
            if (Array.isArray(allResponseData?.data)) {
              balancesList = allResponseData.data;
            } else if (Array.isArray(allResponseData)) {
              balancesList = allResponseData;
            } else if (Array.isArray(allResponse)) {
              balancesList = allResponse;
            } else if (allResponseData && typeof allResponseData === 'object') {
              // Check if it's a single object with id (like {id: 27, ...})
              if (allResponseData.id && !Array.isArray(allResponseData)) {
                balancesList = [allResponseData];
              } else if (Array.isArray(allResponseData.items)) {
                balancesList = allResponseData.items;
              } else if (Array.isArray(allResponseData.opening_balances)) {
                balancesList = allResponseData.opening_balances;
              } else if (Array.isArray(allResponseData.data)) {
                balancesList = allResponseData.data;
              }
            }
            setAllBalancesForPDF(balancesList);
            return balancesList;
          }
        }
      }
      return [];
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    fetchOpeningBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, i18nHook.language]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(typeof window !== "undefined" ? window.innerWidth : 1200);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      fetchOpeningBalances();
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const normalizeDetails = (rawDetails) => {
    if (!rawDetails) return [];

    if (Array.isArray(rawDetails)) {
      return rawDetails.map((detail) => ({
        id: detail.id,
        account_id: detail.account_id || detail.accountId || detail.id || "",
        account_name: detail.account_name || detail.account?.name_ar || detail.account?.name || "",
        account_code: detail.account_code || detail.account?.code || detail.code || "",
        account: detail.account || null,
        debit:
          detail.debit !== undefined && detail.debit !== null
            ? Number(detail.debit)
            : Number(detail.debit_amount || 0),
        credit:
          detail.credit !== undefined && detail.credit !== null
            ? Number(detail.credit)
            : Number(detail.credit_amount || 0),
        line_description:
          detail.line_description || detail.description || detail.note || detail.line_description || "",
      }));
    }

    if (typeof rawDetails === "string") {
      try {
        const parsed = JSON.parse(rawDetails);
        return normalizeDetails(parsed);
      } catch {
        return [];
      }
    }

    if (typeof rawDetails === "object") {
      return Object.entries(rawDetails).map(([accountId, value]) => ({
        id: value?.id,
        account_id: Number(accountId),
        account_name: value?.account_name || value?.account?.name_ar || value?.account?.name || "",
        account_code: value?.account_code || value?.account?.code || value?.code || "",
        account: value?.account || null,
        debit: Number(value?.debit || value?.debit_amount || 0),
        credit: Number(value?.credit || value?.credit_amount || 0),
        line_description:
          value?.line_description || value?.description || value?.note || "",
      }));
    }

    return [];
  };

  const normalizeAttachments = (rawAttachments) => {
    if (!rawAttachments) return [];

    if (Array.isArray(rawAttachments)) {
      return rawAttachments.map((attachment) => ({
        id: attachment.id || attachment.attachment_id || attachment.file_name || attachment.url,
        name: attachment.original_name || attachment.file_name || attachment.name || "مرفق",
        url:
          attachment.url ||
          attachment.file_url ||
          attachment.path ||
          attachment.download_url ||
          null,
        uploaded_at: attachment.created_at || attachment.uploaded_at || null,
      }));
    }

    if (typeof rawAttachments === "object") {
      return Object.entries(rawAttachments).map(([key, value]) => ({
        id: value?.id || key,
        name: value?.original_name || value?.file_name || `مرفق-${key}`,
        url: value?.url || value?.file_url || value?.path || null,
        uploaded_at: value?.created_at || value?.uploaded_at || null,
      }));
    }

    return [];
  };

  // Shared function to build a normalized record (used in both table and PDF templates)
  const buildRecord = (record) => {
    if (!record) return null;

    const normalizedDetails = normalizeDetails(
      record.details ||
      record.entry?.details ||
      record.data?.details ||
      record.data?.entry?.details
    );

    const normalizedAttachments = normalizeAttachments(
      record.attachments ||
      record.entry?.attachments ||
      record.data?.attachments ||
      record.data?.entry?.attachments
    );

    const summary =
      record.summary ||
      record.data?.summary ||
      record.entry?.summary ||
      record.data?.entry?.summary ||
      null;

    const base = record.entry || record.data?.entry || record;

    return {
      id: base.id || record.id,
      date: base.date || base.entry_date || record.date || null,
      entry_date: base.entry_date || base.date || record.entry_date || null,
      description:
        base.description ||
        record.description ||
        record.reference ||
        "",
      status: base.status || record.status || null,
      total_debit:
        base.total_debit ||
        record.total_debit ||
        summary?.total_debit ||
        0,
      total_credit:
        base.total_credit ||
        record.total_credit ||
        summary?.total_credit ||
        0,
      summary,
      details: normalizedDetails,
      attachments: normalizedAttachments,
      raw: record,
    };
  };

  // Local pagination - ensure max 10 items per page (like JournalEntries)
  // allOpeningBalances contains ALL balances fetched with per_page = total
  const filteredOpeningBalances = allOpeningBalances || [];

  const SORT_FIELDS = { DATE: 'date', DESCRIPTION: 'description', STATUS: 'status', TOTAL_DEBIT: 'total_debit', TOTAL_CREDIT: 'total_credit' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  const sortedOpeningBalances = useMemo(() => {
    if (!sortField) return filteredOpeningBalances;
    return [...filteredOpeningBalances].sort((a, b) => {
      let aVal = a[sortField] ?? a['entry_date'] ?? '';
      let bVal = b[sortField] ?? b['entry_date'] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredOpeningBalances, sortField, sortOrder]);

  // Ensure currentPage is valid (between 1 and totalPages)
  const currentPageSafe = Math.max(1, Math.min(currentPage, Math.ceil((filteredOpeningBalances.length || 0) / pageSize) || 1));
  // Calculate total pages based on all balances and pageSize (10)
  const totalPages = Math.ceil((filteredOpeningBalances.length || 0) / pageSize);
  // Calculate start and end indices for current page (max 10 items)
  const startIndex = (currentPageSafe - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedOpeningBalances.length);
  // Slice to get only 10 items for current page
  const paginatedOpeningBalances = sortedOpeningBalances.slice(startIndex, endIndex);

  const openingBalanceItems = useMemo(() => {
    const buildRecord = (record) => {
      if (!record) return null;

      const normalizedDetails = normalizeDetails(
        record.details ||
        record.entry?.details ||
        record.data?.details ||
        record.data?.entry?.details
      );

      const normalizedAttachments = normalizeAttachments(
        record.attachments ||
        record.entry?.attachments ||
        record.data?.attachments ||
        record.data?.entry?.attachments
      );

      const summary =
        record.summary ||
        record.data?.summary ||
        record.entry?.summary ||
        record.data?.entry?.summary ||
        null;

      const base = record.entry || record.data?.entry || record;

      return {
        id: base.id || record.id,
        date: base.date || base.entry_date || record.date || null,
        entry_date: base.entry_date || base.date || record.entry_date || null,
        description:
          base.description ||
          record.description ||
          record.reference ||
          "",
        status: base.status || record.status || null,
        total_debit:
          base.total_debit ||
          record.total_debit ||
          summary?.total_debit ||
          0,
        total_credit:
          base.total_credit ||
          record.total_credit ||
          summary?.total_credit ||
          0,
        summary,
        details: normalizedDetails,
        attachments: normalizedAttachments,
        raw: record,
      };
    };

    // Use allOpeningBalances if available, otherwise fall back to openingBalances from Redux
    // openingBalances from Redux might be in data.data structure
    const reduxBalances = openingBalances;
    let reduxBalancesArray = [];
    if (Array.isArray(reduxBalances)) {
      reduxBalancesArray = reduxBalances;
    } else if (reduxBalances?.data && Array.isArray(reduxBalances.data)) {
      reduxBalancesArray = reduxBalances.data;
    } else if (reduxBalances?.data?.data && Array.isArray(reduxBalances.data.data)) {
      reduxBalancesArray = reduxBalances.data.data;
    }

    const source = allOpeningBalances.length > 0 ? allOpeningBalances : reduxBalancesArray;

    if (Array.isArray(source)) return source.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.data?.data))
      return source.data.data.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.data))
      return source.data.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.items))
      return source.items.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.opening_balances))
      return source.opening_balances.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.entries))
      return source.entries.map(buildRecord).filter(Boolean);
    if (Array.isArray(source?.data?.entries))
      return source.data.entries.map(buildRecord).filter(Boolean);

    if (source?.data?.entry) {
      return [buildRecord(source.data.entry)].filter(Boolean);
    }

    const detailsObject = source?.data?.details || source?.details;
    if (
      detailsObject &&
      typeof detailsObject === "object" &&
      !Array.isArray(detailsObject)
    ) {
      const baseEntry = source?.data?.entry || source?.entry || {};
      const summary = source?.data?.summary || source?.summary || {};
      return Object.entries(detailsObject)
        .map(([accountId, value]) =>
          buildRecord({
            id: Number(accountId),
            entry: {
              id: Number(accountId),
              entry_date: baseEntry.entry_date || baseEntry.date || null,
              description: baseEntry.description || "",
              status: baseEntry.status || null,
              total_debit: Number(value?.debit || 0),
              total_credit: Number(value?.credit || 0),
              summary,
              details: [
                {
                  account_id: Number(accountId),
                  debit: Number(value?.debit || 0),
                  credit: Number(value?.credit || 0),
                },
              ],
            },
          })
        )
        .filter(Boolean);
    }

    if (
      source &&
      typeof source === "object" &&
      !Array.isArray(source) &&
      (source.id !== undefined ||
        source.entry_date ||
        source.details ||
        source.total_debit !== undefined ||
        source.total_credit !== undefined)
    ) {
      return [buildRecord(source)].filter(Boolean);
    }

    return [];
  }, [allOpeningBalances, openingBalances]);

  const paginationMeta = useMemo(() => {
    return (
      pagination ||
      openingBalancesPagination ||
      openingBalances?.data?.meta ||
      openingBalances?.meta ||
      openingBalances?.pagination ||
      null
    );
  }, [pagination, openingBalancesPagination, openingBalances]);

  const totals = useMemo(() => {
    const items = filteredOpeningBalances.length > 0 ? openingBalanceItems : [];
    const totalDebit = items.reduce(
      (sum, item) =>
        sum +
        Number(
          item.total_debit ??
          item.summary?.total_debit ??
          item.totals?.debit ??
          0
        ),
      0
    );
    const totalCredit = items.reduce(
      (sum, item) =>
        sum +
        Number(
          item.total_credit ??
          item.summary?.total_credit ??
          item.totals?.credit ??
          0
        ),
      0
    );
    return { totalDebit, totalCredit };
  }, [openingBalanceItems, filteredOpeningBalances]);

  const handleDelete = (id) => {
    const balanceToDelete = allOpeningBalances.find(b => b.id === id) || openingBalanceItems.find(b => b.id === id);
    setOpeningBalanceToDelete(balanceToDelete);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (openingBalanceToDelete?.id) {
      dispatch(deleteOpeningBalance(openingBalanceToDelete.id));
      setShowDeleteModal(false);
      setOpeningBalanceToDelete(null);
    }
  };

  const handlePost = (id) => {
    dispatch(postOpeningBalance(id));
  };

  const openAttachmentsModal = (attachments) => {
    const safeAttachments = Array.isArray(attachments) ? attachments : [];
    setSelectedAttachments(safeAttachments);
    setAttachmentsModalOpen(true);
  };

  const closeAttachmentsModal = () => {
    setAttachmentsModalOpen(false);
    setSelectedAttachments([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    const locale = 'en-US';
    return parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle print single opening balance
  const handlePrintOpeningBalance = async (openingBalance) => {
    if (!openingBalance) return;

    try {
      setIsGeneratingPdf(true);

      // Get raw data if available, otherwise use the balance itself
      let rawData = openingBalance.raw || openingBalance;

      // Process data using buildRecord to match table format
      const processedBalance = buildRecord(rawData);
      if (!processedBalance) {
        toast.error(t('openingBalances.print.error'), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Create a temporary container for the PDF template
      const tempContainer = document.createElement('div');
      const balanceId = processedBalance.id || openingBalance.id || Date.now();
      tempContainer.id = `opening-balance-pdf-single-${balanceId}`;
      tempContainer.setAttribute('aria-hidden', 'true');
      tempContainer.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 794px; min-height: 1123px; height: auto; background-color: #ffffff; color: #000000; font-family: "Times New Roman Bold", "Times New Roman", "Times", serif; font-weight: bold; padding: 40px; box-sizing: border-box; overflow: visible;';
      document.body.appendChild(tempContainer);

      // Format date
      const dateValue = processedBalance.date || processedBalance.entry_date;
      const formattedDate = dateValue ? (() => {
        try {
          const date = new Date(dateValue);
          return convertArabicNumbersToEnglish(date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }));
        } catch {
          return dateValue;
        }
      })() : '—';

      // Format amounts
      const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return convertArabicNumbersToEnglish(num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      };

      // Build details table rows
      const detailsRows = (processedBalance.details || []).map((detail, idx) => {
        const accountName = isRTL
          ? (detail.account_name || detail.account?.name_ar || detail.account?.name || '—')
          : (detail.account_name || detail.account?.name_en || detail.account?.name || detail.account?.name_ar || '—');
        const accountCode = detail.account_code || detail.account?.code || detail.code || '—';
        const cellTextAlign = isRTL ? 'right' : 'left';
        return `
          <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${idx + 1}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; direction: ${isRTL ? 'rtl' : 'ltr'}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${accountName}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${accountCode}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; direction: ${isRTL ? 'rtl' : 'ltr'}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${detail.line_description || detail.description || detail.note || '—'}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${formatCurrency(detail.debit || 0)}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: ${cellTextAlign}; background-color: ${idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)'}">${formatCurrency(detail.credit || 0)}</td>
          </tr>
        `;
      }).join('');

      // Create the PDF template HTML
      tempContainer.innerHTML = `
        <div style="border: 5px solid #000; padding: 3px; background-color: #ffffff; box-sizing: border-box; width: 100%; min-height: 1043px; height: auto; overflow: visible;">
          <div style="border: 2px solid #000; min-height: 1033px; height: 1033px; padding: 18px 18px 10px; position: relative; display: flex; flex-direction: column; font-family: 'Times New Roman Bold', 'Times New Roman', 'Times', serif; font-weight: bold; direction: ${isRTL ? 'rtl' : 'ltr'}; background-color: #ffffff; box-sizing: border-box; width: 100%; overflow: visible;">
            <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-image: url(${headerLogo}); background-size: 95%; background-position: center center; background-repeat: no-repeat; opacity: 0.08; z-index: 0; pointer-events: none;"></div>
            <div style="position: relative; z-index: 1; display: flex; flex-direction: column; height: 100%; min-height: 100%;">
              <img src="${publicUrl}/HeaderAce.png" alt="Header" style="width: 100%; height: auto; object-fit: contain; margin-bottom: 8px; border-bottom: 4px double #000; padding-bottom: 8px; flex-shrink: 0;" crossOrigin="anonymous" />
              <div style="flex: 1; display: flex; flex-direction: column; overflow: visible; min-height: 0; padding-bottom: 120px;">
                <div style="margin-top: 12px; text-align: center; margin-bottom: 20px;">
                  <h2 style="font-size: 20px; font-weight: 700; color: #000080; margin: 0; direction: ${dir};">${t('openingBalances.title')}</h2>
                </div>
                <div style="direction: ${isRTL ? 'rtl' : 'ltr'}; font-family: 'Times New Roman', 'Times', serif;">
                  <div style="border: 1px solid #000; padding: 10px; margin-bottom: 10px; direction: ${isRTL ? 'rtl' : 'ltr'}">
                    <p style="margin: 5px 0; font-size: 14px; text-align: ${isRTL ? 'right' : 'left'}">
                      <strong>${t('openingBalances.tableHeaders.date')}:</strong> ${formattedDate}
                    </p>
                    <p style="margin: 5px 0; font-size: 14px; text-align: ${isRTL ? 'right' : 'left'}">
                      <strong>${t('openingBalances.tableHeaders.description')}:</strong> ${processedBalance.description || '—'}
                    </p>
                    <p style="margin: 5px 0; font-size: 14px; text-align: ${isRTL ? 'right' : 'left'}">
                      <strong>${t('openingBalances.tableHeaders.totalDebit')}:</strong> ${formatCurrency(processedBalance.total_debit || 0)}
                    </p>
                    <p style="margin: 5px 0; font-size: 14px; text-align: ${isRTL ? 'right' : 'left'}">
                      <strong>${t('openingBalances.tableHeaders.totalCredit')}:</strong> ${formatCurrency(processedBalance.total_credit || 0)}
                    </p>
                    <p style="margin: 5px 0; font-size: 14px; text-align: ${isRTL ? 'right' : 'left'}">
                      <strong>${t('openingBalances.tableHeaders.status')}:</strong> ${statusLabel(processedBalance.status)}
                    </p>
                  </div>
                  ${processedBalance.details && processedBalance.details.length > 0 ? `
                    <div style="margin-top: 20px;">
                      <h3 style="font-size: 16px; font-weight: 700; color: #000080; margin: 0 0 10px; text-align: center;">
                        ${t('openingBalances.details.title', { defaultValue: 'التفاصيل' })}
                      </h3>
                      <table style="width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #000; font-family: 'Times New Roman', 'Times', serif; background-color: rgba(255, 255, 255, 0.7);">
                        <thead>
                          <tr style="background-color: rgba(238, 242, 247, 0.7);">
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.tableHeaders.serialNumber')}</th>
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.details.account', { defaultValue: 'الحساب' })}</th>
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.details.code', { defaultValue: 'الكود' })}</th>
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.details.description', { defaultValue: 'الوصف' })}</th>
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.details.debit', { defaultValue: 'مدين' })}</th>
                            <th style="border: 1px solid #000; padding: 6px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('openingBalances.details.credit', { defaultValue: 'دائن' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${detailsRows}
                        </tbody>
                      </table>
                    </div>
                  ` : ''}
                </div>
              </div>
            </div>
            <div style="position: absolute; bottom: 10px; left: 18px; right: 18px; flex-shrink: 0;">
              <img src="${publicUrl}/FooterAce.png" alt="Footer" style="width: 100%; height: auto; object-fit: contain; border-top: 4px double #000; padding-top: 8px; flex-shrink: 0;" crossOrigin="anonymous" />
            </div>
          </div>
        </div>
      `;

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);

      await waitForRender(800);

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: tempContainer.id,
        fileName: datedFileName(`${t('openingBalances.excel.fileName')}_${balanceId}`),
        isRTL,
      });

      // Clean up
      document.body.removeChild(tempContainer);

      toast.success(t('openingBalances.print.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('openingBalances.print.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle preview all opening balances (like JournalEntries)
  const handlePreviewAll = () => {
    if (!allOpeningBalances || allOpeningBalances.length === 0) {
      toast.error(t('openingBalances.print.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }
    // Process data using buildRecord to match table format
    const processedBalances = allOpeningBalances.map(buildRecord).filter(Boolean);
    if (!processedBalances || processedBalances.length === 0) {
      toast.error(t('openingBalances.print.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }
    const chunks = buildDynamicPdfChunks(processedBalances);
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
  };

  // Handle preview single opening balance
  const handlePreviewSingle = (openingBalance) => {
    if (!openingBalance) {
      toast.error(t('openingBalances.print.noData'), { rtl: isRTL });
      return;
    }

    try {
      // paginatedOpeningBalances comes from allOpeningBalances (raw data from API)
      // So we need to process it using buildRecord
      // If record has raw property, it means it was already processed, so use raw
      let rawData = openingBalance.raw || openingBalance;

      // Process the raw data using buildRecord
      const processedBalance = buildRecord(rawData);

      if (!processedBalance || !processedBalance.id) {
        toast.error(t('openingBalances.print.error'), { rtl: isRTL });
        return;
      }

      setOpeningBalanceForPreview(processedBalance);
      setIsSinglePreviewOpen(true);
    } catch (error) {
      toast.error(t('openingBalances.print.error'), { rtl: isRTL });
    }
  };

  // Preload image helper
  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  const buildDynamicPdfChunks = (balances) => {
    const topHeader = document.getElementById('opening-balances-pdf-measure-top-header');
    const thead = document.getElementById('opening-balances-pdf-measure-thead');
    const footer = document.getElementById('opening-balances-pdf-measure-footer');
    const tbody = document.getElementById('opening-balances-pdf-measure-tbody');
    return calculatePdfPagination(balances, { topHeader, thead, footer, tbody });
  };

  // Handle print all opening balances (like JournalEntries)
  const handlePrintAll = async () => {
    if (!allOpeningBalances || allOpeningBalances.length === 0) {
      toast.error(t('openingBalances.print.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      // Process data using buildRecord to match table format
      const processedBalances = allOpeningBalances.map(buildRecord).filter(Boolean);
      if (!processedBalances || processedBalances.length === 0) {
        toast.error(t('openingBalances.print.noData'), {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        setIsGeneratingPdf(false);
        return;
      }
      const chunks = buildDynamicPdfChunks(processedBalances);
      setAllBalancesForPDF(processedBalances);
      setPdfChunks(chunks);
      setPdfType('all');

      // Wait for DOM to update
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);

      await waitForRender(800);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `opening-balances-pdf-all-${i}`),
        fileName: datedFileName(t('openingBalances.excel.fileName')),
        isRTL,
      });
      toast.success(t('openingBalances.print.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('openingBalances.print.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
    }
  };

  // Handle export single opening balance to Excel
  const handleExportOpeningBalanceToExcel = (openingBalance) => {
    const data = [{
      [t('openingBalances.tableHeaders.date')]: formatDate(openingBalance.date || openingBalance.entry_date) || '—',
      [t('openingBalances.tableHeaders.description')]: openingBalance.description || '—',
      [t('openingBalances.tableHeaders.totalDebit')]: formatCurrency(openingBalance.total_debit),
      [t('openingBalances.tableHeaders.totalCredit')]: formatCurrency(openingBalance.total_credit),
      [t('openingBalances.tableHeaders.status')]: statusLabel(openingBalance.status)
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('openingBalances.excel.sheetName'));
    const identifier = openingBalance.id;
    const fileName = `${t('openingBalances.excel.fileName')}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('openingBalances.export.success'), {
      rtl: isRTL,
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };

  // Handle export all opening balances to Excel (like JournalEntries)
  const handleExportAllToExcel = () => {
    if (!filteredOpeningBalances || filteredOpeningBalances.length === 0) {
      toast.error(t('openingBalances.export.noData'), { rtl: isRTL });
      return;
    }

    try {
      // Process data using buildRecord to match table format
      const allBalanceRecords = filteredOpeningBalances.map(buildRecord).filter(Boolean);
      if (!allBalanceRecords || allBalanceRecords.length === 0) {
        toast.error(t('openingBalances.export.noData'), { rtl: isRTL });
        return;
      }

      const data = allBalanceRecords.map((openingBalance, index) => ({
        [t('openingBalances.tableHeaders.serialNumber')]: index + 1,
        [t('openingBalances.tableHeaders.date')]: formatDate(openingBalance.date || openingBalance.entry_date) || '—',
        [t('openingBalances.tableHeaders.description')]: openingBalance.description || '—',
        [t('openingBalances.tableHeaders.totalDebit')]: formatCurrency(openingBalance.total_debit),
        [t('openingBalances.tableHeaders.totalCredit')]: formatCurrency(openingBalance.total_credit),
        [t('openingBalances.tableHeaders.status')]: statusLabel(openingBalance.status)
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('openingBalances.excel.sheetName'));
      const fileName = `${t('openingBalances.excel.fileName')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('openingBalances.export.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (err) {
      toast.error(t('openingBalances.export.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const statusLabel = (status) => {
    return t(`openingBalances.status.${status || 'draft'}`, { defaultValue: status || '—' });
  };

  const statusColor = (status) => {
    switch (status) {
      case "posted":
        return "#22c55e";
      case "draft":
        return "#9ca3af";
      default:
        return "#6366f1";
    }
  };

  const renderPagination = () => {
    if (totalPages === 0) {
      return null;
    }

    const current = currentPageSafe;
    const last = totalPages;

    return (
      <div
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          borderTop: "1px solid #333",
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}
      >
        {current > 1 && (
          <button
            onClick={() => setCurrentPage(1)}
            style={paginationButtonStyle}
            title={t('common.firstPage')}
          >
            {isRTL ? '>>' : '<<'}
          </button>
        )}
        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
          let page;
          if (totalPages <= 10) {
            page = i + 1;
          } else if (current <= 5) {
            page = i + 1;
          } else if (current >= totalPages - 4) {
            page = totalPages - 9 + i;
          } else {
            page = current - 4 + i;
          }
          return page;
        }).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              ...paginationButtonStyle,
              backgroundColor: page === current ? "#AC2000" : "#1b3350",
              border: page === current ? "none" : "1px solid #333",
              fontWeight: page === current ? "bold" : "normal",
            }}
          >
            {page}
          </button>
        ))}
        {current < last && (
          <button
            onClick={() => setCurrentPage(last)}
            style={paginationButtonStyle}
            title={t('common.lastPage')}
          >
            {isRTL ? '<<' : '>>'}
          </button>
        )}
        <span style={{ color: '#9ca3af', fontSize: '14px', marginLeft: '10px' }}>
          {t('common.pageInfo', {
            current: currentPageSafe,
            total: totalPages
          })}
        </span>
      </div>
    );
  };

  const paginationButtonStyle = {
    backgroundColor: "#1b3350",
    color: "white",
    border: "1px solid #333",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  };

  // Memoize table headers to avoid conditional hook call
  const tableHeaders = useMemo(() => [
    { label: t('openingBalances.tableHeaders.serialNumber'), width: "60px" },
    { label: t('openingBalances.tableHeaders.date'), width: "140px", sortKey: SORT_FIELDS.DATE },
    { label: t('openingBalances.tableHeaders.description'), width: "220px", sortKey: SORT_FIELDS.DESCRIPTION },
    { label: t('openingBalances.tableHeaders.status'), width: "120px", sortKey: SORT_FIELDS.STATUS },
    { label: t('openingBalances.tableHeaders.totalDebit'), width: "160px", sortKey: SORT_FIELDS.TOTAL_DEBIT },
    { label: t('openingBalances.tableHeaders.totalCredit'), width: "160px", sortKey: SORT_FIELDS.TOTAL_CREDIT },
    { label: t('openingBalances.tableHeaders.detailsCount'), width: "140px" },
    { label: t('openingBalances.tableHeaders.attachments'), width: "140px" },
    { label: t('openingBalances.tableHeaders.actions'), width: "200px" },
  ], [t, i18nHook.language]);

  const toggleRowExpansion = (recordId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  if (isLoading && (!allOpeningBalances || allOpeningBalances.length === 0)) {
    return (
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: dir }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          direction: dir
        }}
      >
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: "12px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div>
            <h1 style={{ fontSize: "20px", margin: 0, textAlign }}>{t('openingBalances.title')}</h1>
            <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: "13px", textAlign }}>
              {t('openingBalances.description')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              disabled={!allOpeningBalances || allOpeningBalances.length === 0}
              style={{
                backgroundColor: !allOpeningBalances || allOpeningBalances.length === 0 ? '#666' : '#444',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: !filteredOpeningBalances || filteredOpeningBalances.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaEye /> {t('openingBalances.preview.all')}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf || !allOpeningBalances || allOpeningBalances.length === 0}
              style={{
                backgroundColor: isGeneratingPdf || !allOpeningBalances || allOpeningBalances.length === 0 ? '#666' : '#1f5ff1',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: isGeneratingPdf || !filteredOpeningBalances || filteredOpeningBalances.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('openingBalances.print.generating') : t('openingBalances.print.all')}
            </button>
            <button
              onClick={handleExportAllToExcel}
              disabled={!allOpeningBalances || allOpeningBalances.length === 0}
              style={{
                backgroundColor: !allOpeningBalances || allOpeningBalances.length === 0 ? '#666' : '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: !filteredOpeningBalances || filteredOpeningBalances.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaFileExcel /> {t('openingBalances.export.all')}
            </button>
            <button
              onClick={() => navigate("/opening-balance/add")}
              style={{
                backgroundColor: "#AC2000",
                border: "none",
                color: "white",
                padding: "12px 18px",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaPlus /> {t('openingBalances.add.title')}
            </button>
          </div>
        </div>

        <div
          style={{
            marginBottom: "16px",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: "12px",
          }}
        >
          <div
            style={{
              backgroundColor: "#1F2937",
              borderRadius: "12px",
              border: "1px solid #333",
              padding: "16px",
            }}
          >
            <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af", textAlign }}>
              {t('openingBalances.totals.totalDebit')}
            </p>
            <h3 style={{ margin: "6px 0 0", fontSize: "20px", textAlign: 'center' }}>
              {convertArabicNumbersToEnglish(totals.totalDebit.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }))}
            </h3>
          </div>
          <div
            style={{
              backgroundColor: "#1F2937",
              borderRadius: "12px",
              border: "1px solid #333",
              padding: "16px",
            }}
          >
            <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af", textAlign }}>
              {t('openingBalances.totals.totalCredit')}
            </p>
            <h3 style={{ margin: "6px 0 0", fontSize: "20px", textAlign: 'center' }}>
              {convertArabicNumbersToEnglish(totals.totalCredit.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }))}
            </h3>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#1b3350",
            border: "1px solid #333",
            borderRadius: "12px",
            overflow: "hidden",
            direction: dir
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, direction: dir }}
            >
              <thead>
                <tr style={{ backgroundColor: "#AC2000" }}>
                  {tableHeaders.map((col) => (
                    <th
                      key={col.label}
                      onClick={col.sortKey ? () => handleSort(col.sortKey) : undefined}
                      style={{
                        padding: "18px 16px",
                        color: "white",
                        borderBottom: "2px solid #333",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textAlign: "center",
                        width: col.width,
                        minWidth: col.width,
                        backgroundColor: "#AC2000",
                        cursor: col.sortKey ? 'pointer' : undefined,
                      }}
                    >
                      {col.label}{col.sortKey ? getSortIcon(col.sortKey) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedOpeningBalances.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#9ca3af",
                        fontSize: "16px",
                        direction: dir
                      }}
                    >
                      {t('openingBalances.noData')}
                    </td>
                  </tr>
                ) : (
                  paginatedOpeningBalances.map((record, idx) => {
                    const normalizedDetails = normalizeDetails(record.details);
                    const detailsCount = normalizedDetails.length;
                    const attachments = record.attachments || [];
                    const totalDebit = Number(
                      record.total_debit ??
                      record.summary?.total_debit ??
                      record.totals?.debit ??
                      0
                    );
                    const totalCredit = Number(
                      record.total_credit ??
                      record.summary?.total_credit ??
                      record.totals?.credit ??
                      0
                    );
                    const status = record.status || record.state;
                    const isDraft = status === "draft";
                    const isPosted = status === "posted";
                    const isExpanded = expandedRows.has(record.id);

                    return (
                      <React.Fragment key={record.id || idx}>
                        <tr
                          style={{
                            borderBottom: "1px solid #333",
                            backgroundColor: idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(ev) => {
                            const row = ev.currentTarget;
                            row.style.backgroundColor = "#2a3441";
                          }}
                          onMouseLeave={(ev) => {
                            const row = ev.currentTarget;
                            row.style.backgroundColor =
                              idx % 2 === 0 ? "#1b3350" : "#1a1f2e";
                          }}
                        >
                          <td style={tableCellStyle}> {openingBalancesPagination?.total - (startIndex + idx + 1) + 1}</td>
                          <td style={tableCellStyle}>
                            {formatDate(record.date || record.entry_date)}
                          </td>
                          <td style={{ ...tableCellStyle, textAlign: 'center', direction: dir }}>
                            {record.description ||
                              record.reference ||
                              record.memo ||
                              (typeof record.summary === "object"
                                ? record.summary?.note || record.summary?.description
                                : record.summary) ||
                              "—"}
                          </td>
                          <td style={tableCellStyle}>
                            <span
                              style={{
                                backgroundColor: statusColor(status),
                                color: "white",
                                padding: "6px 14px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "bold",
                                display: "inline-block",
                              }}
                            >
                              {statusLabel(status)}
                            </span>
                          </td>
                          <td style={{ ...tableCellStyle, fontWeight: 600 }}>
                            {convertArabicNumbersToEnglish(totalDebit.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }))}
                          </td>
                          <td style={{ ...tableCellStyle, fontWeight: 600 }}>
                            {convertArabicNumbersToEnglish(totalCredit.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }))}
                          </td>
                          <td style={tableCellStyle}>{detailsCount}</td>
                          <td style={{ ...tableCellStyle, position: "relative" }}>
                            {attachments.length === 0 ? (<>
                              <div className="hidden"></div>
                            </>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                  alignItems: "center",
                                }}
                              >
                                {/* <span>{attachments.length}</span> */}
                                <button
                                  title={t('openingBalances.attachments.open', { defaultValue: 'عرض المرفقات' })}
                                  onClick={() => openAttachmentsModal(attachments)}
                                  style={{
                                    backgroundColor: '#4b5563',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#6b7280';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#4b5563';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <FaPaperclip />
                                  {t('openingBalances.attachments.button', { defaultValue: 'مرفقات' })}
                                </button>
                              </div>
                            )}
                          </td>
                          <td style={tableCellStyle}>
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center",
                                alignItems: "center",
                                flexWrap: "nowrap",
                              }}
                            >
                              <button
                                title={t('openingBalances.preview.single')}
                                onClick={() => handlePreviewSingle(record)}
                                style={actionButtonStyle("#0d6efd")}
                                onMouseEnter={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "#0b5ed7";
                                  ev.currentTarget.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "#0d6efd";
                                  ev.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                <FaEye />
                              </button>
                              <button
                                title={t('openingBalances.print.single')}
                                onClick={() => handlePrintOpeningBalance(record)}
                                disabled={isGeneratingPdf}
                                style={{
                                  ...actionButtonStyle(isGeneratingPdf ? "#666" : "#1f5ff1"),
                                  opacity: isGeneratingPdf ? 0.6 : 1,
                                  cursor: isGeneratingPdf ? 'not-allowed' : 'pointer'
                                }}
                                onMouseEnter={(ev) => {
                                  if (!isGeneratingPdf) {
                                    ev.currentTarget.style.backgroundColor = "#0b5ed7";
                                    ev.currentTarget.style.transform = "scale(1.05)";
                                  }
                                }}
                                onMouseLeave={(ev) => {
                                  if (!isGeneratingPdf) {
                                    ev.currentTarget.style.backgroundColor = "#1f5ff1";
                                    ev.currentTarget.style.transform = "scale(1)";
                                  }
                                }}
                              >
                                <FaPrint />
                              </button>
                              <button
                                title={t('openingBalances.export.single')}
                                onClick={() => handleExportOpeningBalanceToExcel(record)}
                                style={actionButtonStyle("#27ae60")}
                                onMouseEnter={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "#229954";
                                  ev.currentTarget.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "#27ae60";
                                  ev.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                <FaFileExcel />
                              </button>
                              {isDraft && (
                                <>
                                  <button
                                    title={t('openingBalances.actions.edit')}
                                    onClick={() =>
                                      navigate(`/opening-balance/edit/${record.id}`)
                                    }
                                    style={actionButtonStyle("#9ca3af")}
                                    onMouseEnter={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#7f8597";
                                      ev.currentTarget.style.transform = "scale(1.05)";
                                    }}
                                    onMouseLeave={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#9ca3af";
                                      ev.currentTarget.style.transform = "scale(1)";
                                    }}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    title={t('openingBalances.actions.post')}
                                    onClick={() => handlePost(record.id)}
                                    style={actionButtonStyle("#845ef7")}
                                    onMouseEnter={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#7950f2";
                                      ev.currentTarget.style.transform = "scale(1.05)";
                                    }}
                                    onMouseLeave={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#845ef7";
                                      ev.currentTarget.style.transform = "scale(1)";
                                    }}
                                  >
                                    <FaPaperPlane />
                                  </button>
                                  <button
                                    title={t('openingBalance.action.delete')}
                                    onClick={() => handleDelete(record.id)}
                                    style={actionButtonStyle("#dc3545")}
                                    onMouseEnter={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#bb2d3b";
                                      ev.currentTarget.style.transform = "scale(1.05)";
                                    }}
                                    onMouseLeave={(ev) => {
                                      ev.currentTarget.style.backgroundColor = "#dc3545";
                                      ev.currentTarget.style.transform = "scale(1)";
                                    }}
                                  >
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && normalizedDetails.length > 0 && (
                          <tr>
                            <td colSpan={9} style={{ padding: '0', backgroundColor: '#1a1f2e' }}>
                              <div style={{ padding: '20px', backgroundColor: '#1a1f2e' }}>
                                <h4 style={{ margin: '0 0 15px', fontSize: '14px', fontWeight: 'bold', color: '#60a5fa', textAlign }}>
                                  {t('openingBalances.details.title', { defaultValue: 'Details' })}
                                </h4>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, direction: dir }}>
                                    <thead>
                                      <tr style={{ backgroundColor: '#2a3441' }}>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '60px' }}>
                                          {t('openingBalances.tableHeaders.serialNumber')}
                                        </th>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '200px', textAlign }}>
                                          {t('openingBalances.details.account', { defaultValue: 'Account' })}
                                        </th>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '120px' }}>
                                          {t('openingBalances.details.code', { defaultValue: 'Code' })}
                                        </th>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '220px', textAlign }}>
                                          {t('openingBalances.details.description', { defaultValue: 'Description' })}
                                        </th>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '150px' }}>
                                          {t('openingBalances.details.debit', { defaultValue: 'Debit' })}
                                        </th>
                                        <th style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', width: '150px' }}>
                                          {t('openingBalances.details.credit', { defaultValue: 'Credit' })}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {normalizedDetails.map((detail, detailIdx) => {
                                        const accountName = detail.account_name || detail.account?.name_ar || detail.account?.name || (isRTL ? detail.account?.name_ar : detail.account?.name_en) || '—';
                                        const accountCode = detail.account_code || detail.account?.code || detail.code || '—';
                                        return (
                                          <tr
                                            key={detail.id || detailIdx}
                                            style={{
                                              borderBottom: '1px solid #333',
                                              backgroundColor: detailIdx % 2 === 0 ? '#1b3350' : '#1a1f2e'
                                            }}
                                          >
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px' }}>{detailIdx + 1}</td>
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', textAlign, direction: dir }}>{accountName}</td>
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px' }}>{accountCode}</td>
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', textAlign, direction: dir }}>
                                              {detail.line_description || detail.description || detail.note || '—'}
                                            </td>
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', fontWeight: 600, color: '#facc15' }}>
                                              {formatCurrency(detail.debit || 0)}
                                            </td>
                                            <td style={{ ...tableCellStyle, padding: '12px', fontSize: '12px', fontWeight: 600, color: '#60a5fa' }}>
                                              {formatCurrency(detail.credit || 0)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>

        {/* Print Templates for All Opening Balances - Multiple pages */}
        {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
          <AllOpeningBalancesPrintTemplate
            key={chunkIndex}
            chunkIndex={chunkIndex}
            openingBalanceItems={chunk.items || chunk}
            startIndex={chunk.startIndex ?? chunkIndex * 25}
            paginationMeta={null}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            statusLabel={statusLabel}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
          />
        ))}

        {/* Preview Modal for All */}
        <PdfPreviewModal
          isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
          title={t('openingBalances.title')}
          onClose={() => {
            setIsPreviewOpen(false);
            setPdfChunks([]);
          }}
          onPrint={handlePrintAll}
          isGeneratingPdf={isGeneratingPdf}
        >
          {(pdfChunks || []).map((chunk, chunkIndex) => (
            <AllOpeningBalancesPreviewTemplate
              key={chunkIndex}
              chunkIndex={chunkIndex}
              openingBalanceItems={chunk.items || chunk}
              startIndex={chunk.startIndex ?? chunkIndex * 25}
              paginationMeta={null}
              isRTL={isRTL}
              t={t}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              statusLabel={statusLabel}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              totalChunks={pdfChunks?.length || 1}
            />
          ))}
        </PdfPreviewModal>

        {/* Preview Modal for Single */}
        <PdfPreviewModal
          isOpen={isSinglePreviewOpen && openingBalanceForPreview}
          title={t('openingBalances.title')}
          onClose={() => {
            setIsSinglePreviewOpen(false);
            setOpeningBalanceForPreview(null);
          }}
          onPrint={() => openingBalanceForPreview && handlePrintOpeningBalance(openingBalanceForPreview)}
          isGeneratingPdf={isGeneratingPdf}
        >
          {openingBalanceForPreview && (
            <OpeningBalanceSinglePreviewTemplate
              openingBalance={openingBalanceForPreview}
              isRTL={isRTL}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              statusLabel={statusLabel}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              t={t}
            />
          )}
        </PdfPreviewModal>

        {/* Attachments Modal */}
        {attachmentsModalOpen && createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
            onClick={closeAttachmentsModal}
          >
            <div
              style={{
                backgroundColor: '#1f2937',
                border: '1px solid #333',
                borderRadius: 12,
                padding: 20,
                width: '95%',
                maxWidth: 900,
                maxHeight: '90vh',
                overflow: 'auto',
                margin: 'auto',
                direction: dir,
                textAlign,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: 'white' }}>
                {t('openingBalances.attachmentsModal.title', { defaultValue: 'المرفقات' })}
              </h3>

              {selectedAttachments && Array.isArray(selectedAttachments) && selectedAttachments.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {selectedAttachments.map((attachment, idx) => {
                    const fileUrl =
                      attachment?.file_url ||
                      attachment?.url ||
                      attachment?.media_url ||
                      (attachment?.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : '') ||
                      attachment?.full_url ||
                      attachment?.link ||
                      '';

                    const isImage =
                      (attachment.mime_type && attachment.mime_type.startsWith('image/')) ||
                      /\.(png|jpe?g|gif|webp)$/i.test(attachment.file_name || attachment.name || '');

                    return (
                      <div
                        key={attachment.id || idx}
                        style={{
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        {isImage && fileUrl ? (
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '75%',
                              backgroundColor: '#14263b',
                              borderRadius: 4,
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={attachment.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : fileUrl}
                              alt={attachment.file_name || attachment.name || `Attachment ${idx + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'pointer',
                              }}
                              onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errorDiv = e.target.nextSibling;
                                if (errorDiv) errorDiv.style.display = 'flex';
                              }}
                            />
                            <div
                              style={{
                                display: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#888',
                                fontSize: 12,
                              }}
                            >
                              {t('openingBalances.attachmentsModal.imageError', { defaultValue: 'خطأ في تحميل الصورة' })}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              paddingTop: '75%',
                              backgroundColor: '#14263b',
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#888',
                            }}
                          >
                            <FaFileAlt style={{ fontSize: 32 }} />
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 12,
                            color: '#ccc',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                          }}
                        >
                          {attachment.file_name || attachment.name || `Attachment ${idx + 1}`}
                        </div>
                        {fileUrl && (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              backgroundColor: '#AC2000',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              textAlign: 'center',
                              textDecoration: 'none',
                              fontSize: 12,
                              fontWeight: 'bold',
                            }}
                          >
                            {isImage
                              ? t('openingBalances.attachmentsModal.view', { defaultValue: 'عرض' })
                              : t('openingBalances.attachmentsModal.download', { defaultValue: 'تحميل' })}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>
                  {t('openingBalances.attachmentsModal.noAttachments', { defaultValue: 'لا توجد مرفقات' })}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
                <button
                  onClick={closeAttachmentsModal}
                  style={{
                    backgroundColor: '#4b5563',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  {t('openingBalances.attachmentsModal.close', { defaultValue: 'إغلاق' })}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }} onClick={() => {
            setShowDeleteModal(false);
            setOpeningBalanceToDelete(null);
          }}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '400px',
              width: '90%',
              border: '1px solid #333',
              direction: dir
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{
                color: 'white',
                marginBottom: '20px',
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                {t('openingBalances.delete.title', { defaultValue: 'تأكيد الحذف' })}
              </h3>
              <p style={{
                color: 'white',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: '16px'
              }}>
                {t('openingBalances.delete.message', { defaultValue: 'هل أنت متأكد من حذف الرصيد الافتتاحي' })}
              </p>
              <p style={{
                color: '#ef4444',
                marginBottom: '30px',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                {t('openingBalances.delete.warning', { defaultValue: 'هذا الإجراء لا يمكن التراجع عنه.' })}
              </p>
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center',
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setOpeningBalanceToDelete(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#374151';
                  }}
                >
                  {t('openingBalances.delete.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ef4444';
                  }}
                >
                  {t('openingBalances.delete.confirm', { defaultValue: 'حذف' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="opening-balances-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '95%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <h2 style={{ textAlign: 'center', fontSize: '18px', margin: '8px 0' }}>{t('openingBalances.title')}</h2>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
          <thead id="opening-balances-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>{t('openingBalances.tableHeaders.serialNumber')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('openingBalances.tableHeaders.date')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('openingBalances.tableHeaders.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('openingBalances.tableHeaders.totalDebit')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('openingBalances.tableHeaders.totalCredit')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('openingBalances.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody id="opening-balances-pdf-measure-tbody">
            {(paginatedOpeningBalances || []).slice(0, 5).map((balance, idx) => {
              const rec = buildRecord(balance.raw || balance);
              return rec ? (
                <tr key={idx}>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatDate(rec.date)}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{rec.description || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatCurrency(rec.total_debit)}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatCurrency(rec.total_credit)}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{statusLabel(rec.status)}</td>
                </tr>
              ) : null;
            })}
          </tbody>
        </table>
        <div id="opening-balances-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </React.Fragment>
  );
};

const tableCellStyle = {
  padding: "18px 16px",
  textAlign: "center",
  color: "white",
  fontSize: "14px",
};

const actionButtonStyle = (bg) => ({
  backgroundColor: bg,
  color: "white",
  border: "none",
  padding: "10px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
});

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "13px",
  color: "#cbd5f5",
};

const inputStyle = {
  width: "100%",
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  color: "white",
  borderRadius: "8px",
  padding: "10px",
  boxSizing: "border-box",
};

// Print Template for All Opening Balances
const AllOpeningBalancesPrintTemplate = ({ openingBalanceItems, paginationMeta, isRTL, t, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, chunkIndex = 0, startIndex = 0 }) => {
  if (!openingBalanceItems || openingBalanceItems.length === 0) return null;

  return (
    <div
      id={`opening-balances-pdf-all-${chunkIndex}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: isRTL ? 'rtl' : 'ltr' }}>{t('openingBalances.title')}</h2>
                  <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>{new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)', width: '36px' }}>{t('openingBalances.tableHeaders.serialNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.date')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.description')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.totalDebit')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.totalCredit')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openingBalanceItems.map((openingBalance, idx) => {
                      const globalIndex = startIndex + idx;
                      const cellTextAlign = isRTL ? 'right' : 'left';
                      return (
                        <tr key={openingBalance.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', width: '36px' }}>{globalIndex + 1}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(openingBalance.date || openingBalance.entry_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, direction: isRTL ? 'rtl' : 'ltr', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{openingBalance.description || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrency(openingBalance.total_debit)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrency(openingBalance.total_credit)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{statusLabel(openingBalance.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Template for All Opening Balances
const AllOpeningBalancesPreviewTemplate = ({ openingBalanceItems, paginationMeta, isRTL, t, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, chunkIndex = 0, startIndex = 0, totalChunks = 1 }) => {
  if (!openingBalanceItems || openingBalanceItems.length === 0) return null;

  // Wrap formatCurrency and formatDate to always show English numbers
  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      id={`preview-opening-balances-pdf-all-${chunkIndex}`}
      style={{
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: '0 auto',
        overflow: 'visible',
        marginBottom: chunkIndex < totalChunks - 1 ? '20px' : 0
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: isRTL ? 'rtl' : 'ltr' }}>{t('openingBalances.title')}</h2>
                  <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>{new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)', width: '36px' }}>{t('openingBalances.tableHeaders.serialNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.date')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.description')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.totalDebit')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.totalCredit')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openingBalanceItems.map((openingBalance, idx) => {
                      const globalIndex = startIndex + idx;
                      const cellTextAlign = isRTL ? 'right' : 'left';
                      return (
                        <tr key={openingBalance.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', width: '36px' }}>{globalIndex + 1}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDateEnglish(openingBalance.date || openingBalance.entry_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, direction: isRTL ? 'rtl' : 'ltr', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{openingBalance.description || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrencyEnglish(openingBalance.total_debit)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrencyEnglish(openingBalance.total_credit)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{statusLabel(openingBalance.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Opening Balance Preview Template
const OpeningBalanceSinglePreviewTemplate = ({ openingBalance, isRTL, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, t }) => {
  if (!openingBalance) return null;

  // Wrap formatCurrency and formatDate to always show English numbers
  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      id={`preview-opening-balance-pdf-${openingBalance.id}`}
      style={{
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: '0 auto',
        overflow: 'visible'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: 'auto',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: '0 0 20px', textAlign: 'center' }}>
                  {t('openingBalances.title')}
                </h2>
                <div style={{ border: '1px solid #000', padding: '10px', marginBottom: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <p style={{ margin: '5px 0', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    <strong>{t('openingBalances.tableHeaders.date')}:</strong> {formatDateEnglish(openingBalance.date || openingBalance.entry_date) || '—'}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    <strong>{t('openingBalances.tableHeaders.description')}:</strong> {openingBalance.description || '—'}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    <strong>{t('openingBalances.tableHeaders.totalDebit')}:</strong> {formatCurrencyEnglish(openingBalance.total_debit)}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    <strong>{t('openingBalances.tableHeaders.totalCredit')}:</strong> {formatCurrencyEnglish(openingBalance.total_credit)}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    <strong>{t('openingBalances.tableHeaders.status')}:</strong> {statusLabel(openingBalance.status)}
                  </p>
                </div>
                {openingBalance.details && openingBalance.details.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#000080', margin: '0 0 10px', textAlign: 'center' }}>
                      {t('openingBalances.details.title', { defaultValue: 'التفاصيل' })}
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.tableHeaders.serialNumber')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.details.account', { defaultValue: 'الحساب' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.details.code', { defaultValue: 'الكود' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.details.description', { defaultValue: 'الوصف' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.details.debit', { defaultValue: 'مدين' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('openingBalances.details.credit', { defaultValue: 'دائن' })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openingBalance.details.map((detail, detailIdx) => {
                          const accountName = isRTL
                            ? (detail.account_name || detail.account?.name_ar || detail.account?.name || '—')
                            : (detail.account_name || detail.account?.name_en || detail.account?.name || detail.account?.name_ar || '—');
                          const accountCode = detail.account_code || detail.account?.code || detail.code || '—';
                          const cellTextAlign = isRTL ? 'right' : 'left';
                          return (
                            <tr key={detail.id || detailIdx} style={{ backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{detailIdx + 1}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, direction: isRTL ? 'rtl' : 'ltr', backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{accountName}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{accountCode}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, direction: isRTL ? 'rtl' : 'ltr', backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{detail.line_description || detail.description || detail.note || '—'}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrencyEnglish(detail.debit || 0)}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: cellTextAlign, backgroundColor: detailIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrencyEnglish(detail.credit || 0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningBalances;


